'use client'

import type { PageViewData, EngagementData, ScrollData } from './tipos'
import { getVisitorInfo } from './visitante'
import { getDeviceInfo } from './dispositivo'
import { getUTMParams } from './utm'
import { fetchGeoInfo } from './geo'
import { logEvent as firebaseLogEvent } from '@/lib/firebase-client'

// GA4 Measurement ID
export const GA4_MEASUREMENT_ID = process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID || process.env.NEXT_PUBLIC_GA4_ID || 'G-EVXCVZ6T00'

/** Verifica se está em rota admin */
function isAdminRoute(): boolean {
  return typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')
}

// Storage keys
const SESSION_PAGEVIEW_KEY = 'biomo_session_pageview'

/**
 * Envia evento para GA4 via gtag + dataLayer
 */
export function enviarEventoGA4(eventName: string, params?: Record<string, unknown>) {
  if (typeof window === 'undefined') return
  if (isAdminRoute()) return

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ event: eventName, ...params })

  if (window.gtag) {
    window.gtag('event', eventName, params)
  }

  // Envia também via Firebase Analytics SDK
  firebaseLogEvent(eventName, params)
}

/**
 * Envia page_view (1 por sessão/página)
 */
export async function trackPageView() {
  if (typeof window === 'undefined') return

  // Controle: 1 pageview por sessão por página
  const sessionKey = `${SESSION_PAGEVIEW_KEY}_${window.location.pathname}`
  const alreadyTracked = sessionStorage.getItem(sessionKey)

  if (alreadyTracked) return

  const visitor = getVisitorInfo()
  const utm = getUTMParams()
  const geo = await fetchGeoInfo()
  const device = getDeviceInfo()

  const pageViewData: PageViewData = {
    page_location: window.location.href,
    page_title: document.title,
    page_referrer: document.referrer,
    visitor_id: visitor.visitorId,
    visit_count: visitor.visitCount,
    is_new_visitor: visitor.isNewVisitor,
    device_type: device.type,
    browser: device.browser,
    os: device.os,
    screen_resolution: `${device.screen.width}x${device.screen.height}`,
    viewport_size: `${device.viewport.width}x${device.viewport.height}`,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    ...(utm.source && { utm_source: utm.source }),
    ...(utm.medium && { utm_medium: utm.medium }),
    ...(utm.campaign && { utm_campaign: utm.campaign }),
    ...(utm.content && { utm_content: utm.content }),
    ...(utm.term && { utm_term: utm.term }),
    ...(utm.gclid && { gclid: utm.gclid }),
    ...(geo.city && { city: geo.city }),
    ...(geo.region && { region: geo.region }),
    ...(geo.country && { country: geo.country }),
  }

  // Marcar antes de enviar
  sessionStorage.setItem(sessionKey, Date.now().toString())

  enviarEventoGA4('page_view', pageViewData as unknown as Record<string, unknown>)

  // Enviar para servidor
  fetch('/api/analytics/pageview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(pageViewData),
  }).catch(() => {})
}

/**
 * Inicializa scroll tracking com milestones 25/50/75/90/100
 */
export function initScrollTracking() {
  if (typeof window === 'undefined') return

  window._biomoAnalytics = window._biomoAnalytics || {
    pageLoadTime: Date.now(),
    scrollDepths: new Set(),
    clickCount: 0,
    formInteractions: 0,
    lastActivity: Date.now(),
  }

  const trackScroll = () => {
    const scrollTop = window.scrollY
    const docHeight = document.documentElement.scrollHeight - window.innerHeight
    const scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0

    const milestones = [25, 50, 75, 90, 100]
    const tracked = window._biomoAnalytics?.scrollDepths || new Set()

    for (const milestone of milestones) {
      if (scrollPercent >= milestone && !tracked.has(milestone)) {
        tracked.add(milestone)

        const timeOnPage = Math.round(
          (Date.now() - (window._biomoAnalytics?.pageLoadTime || Date.now())) / 1000
        )

        const scrollData: ScrollData = {
          depth: milestone,
          page_location: window.location.href,
          time_on_page: timeOnPage,
        }

        enviarEventoGA4('scroll', scrollData as unknown as Record<string, unknown>)
      }
    }
  }

  let scrollTimeout: NodeJS.Timeout
  window.addEventListener(
    'scroll',
    () => {
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(trackScroll, 100)
    },
    { passive: true }
  )
}

/**
 * Inicializa click tracking
 */
export function initClickTracking() {
  if (typeof window === 'undefined') return

  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement

    if (window._biomoAnalytics) {
      window._biomoAnalytics.clickCount++
      window._biomoAnalytics.lastActivity = Date.now()
    }

    const clickable = target.closest('button, a, [role="button"]')
    if (clickable) {
      const label =
        clickable.textContent?.trim().substring(0, 50) ||
        clickable.getAttribute('aria-label') ||
        clickable.getAttribute('title') ||
        'unknown'

      const href = clickable.getAttribute('href')

      enviarEventoGA4('click', {
        element_type: clickable.tagName.toLowerCase(),
        element_text: label,
        element_href: href,
        page_location: window.location.href,
      })
    }
  })
}

/**
 * Inicializa form interaction tracking
 */
export function initFormTracking() {
  if (typeof window === 'undefined') return

  document.addEventListener('focusin', (e) => {
    const target = e.target as HTMLElement
    if (target.matches('input, textarea, select')) {
      if (window._biomoAnalytics) {
        window._biomoAnalytics.formInteractions++
        window._biomoAnalytics.lastActivity = Date.now()
      }

      enviarEventoGA4('form_field_focus', {
        field_name: target.getAttribute('name') || target.getAttribute('id') || 'unknown',
        field_type: target.getAttribute('type') || target.tagName.toLowerCase(),
      })
    }
  })
}

/**
 * Track outbound links e cliques em WhatsApp
 */
export function initOutboundLinkTracking() {
  if (typeof window === 'undefined') return

  document.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest('a')
    if (!link) return

    const href = link.getAttribute('href')
    if (!href) return

    try {
      const url = new URL(href, window.location.origin)

      if (href.includes('wa.me') || href.includes('whatsapp')) {
        enviarEventoGA4('whatsapp_click', {
          link_url: href,
          link_text: link.textContent?.trim().substring(0, 50),
        })
      } else if (url.hostname !== window.location.hostname) {
        enviarEventoGA4('outbound_click', {
          link_url: href,
          link_domain: url.hostname,
          link_text: link.textContent?.trim().substring(0, 50),
        })
      }
    } catch {
      // URL inválida
    }
  })
}

/**
 * Track engagement ao sair da página via sendBeacon
 */
export function trackEngagementOnExit() {
  if (typeof window === 'undefined') return

  const sendEngagement = () => {
    const analytics = window._biomoAnalytics
    if (!analytics) return

    const totalTime = Math.round((Date.now() - analytics.pageLoadTime) / 1000)
    const engagedTime = Math.round((analytics.lastActivity - analytics.pageLoadTime) / 1000)
    const maxScroll = Math.max(...Array.from(analytics.scrollDepths), 0)

    const engagementData: EngagementData = {
      engaged_time: engagedTime,
      total_time: totalTime,
      scroll_depth: maxScroll,
      click_count: analytics.clickCount,
      form_interactions: analytics.formInteractions,
    }

    const data = {
      event: 'page_engagement',
      ...engagementData,
      page_location: window.location.href,
      visitor_id: getVisitorInfo().visitorId,
    }

    // Push para dataLayer
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(data)

    // Enviar via sendBeacon
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(data)], { type: 'application/json' })
      navigator.sendBeacon('/api/analytics/engagement', blob)
    }
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') sendEngagement()
  })
  window.addEventListener('beforeunload', sendEngagement)
  window.addEventListener('pagehide', sendEngagement)
}

/**
 * Inicializa todos os trackers
 */
export function initEnhancedAnalytics() {
  if (typeof window === 'undefined') return
  if (isAdminRoute()) return

  window.dataLayer = window.dataLayer || []

  trackPageView()
  initScrollTracking()
  initClickTracking()
  initFormTracking()
  initOutboundLinkTracking()
  trackEngagementOnExit()
}

/**
 * Track evento customizado
 */
export function trackEvent(eventName: string, params?: Record<string, unknown>) {
  const visitor = getVisitorInfo()
  enviarEventoGA4(eventName, {
    ...params,
    visitor_id: visitor.visitorId,
    visit_count: visitor.visitCount,
  })
}

/**
 * Track clique no CTA (botão "Fazer orçamento" / "Começar agora")
 * Salva em GA4 + Firebase (analytics_engagement com event: 'cta_click')
 */
export function trackCtaClick(buttonText: string, location: string) {
  if (typeof window === 'undefined') return
  if (isAdminRoute()) return

  const visitor = getVisitorInfo()

  enviarEventoGA4('cta_click', {
    button_text: buttonText,
    button_location: location,
    visitor_id: visitor.visitorId,
  })

  fetch('/api/analytics/engagement', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      event: 'cta_click',
      visitor_id: visitor.visitorId,
      page_location: window.location.href,
      click_count: 0,
      engaged_time: 0,
      scroll_depth: 0,
      form_interactions: 0,
      form_step: null,
      form_step_name: null,
      cta_button_text: buttonText,
      cta_button_location: location,
    }),
  }).catch(() => {})
}

/**
 * Track form step (para funil)
 */
export function trackFormStep(step: number, stepName: string, additionalData?: Record<string, unknown>) {
  const visitor = getVisitorInfo()
  enviarEventoGA4('form_step', {
    step_number: step,
    step_name: stepName,
    visitor_id: visitor.visitorId,
    visit_count: visitor.visitCount,
    ...additionalData,
  })
}

/**
 * Track form complete
 */
export function trackFormComplete(formData?: Record<string, unknown>) {
  const visitor = getVisitorInfo()
  const analytics = window._biomoAnalytics

  const totalTime = analytics
    ? Math.round((Date.now() - analytics.pageLoadTime) / 1000)
    : 0

  enviarEventoGA4('form_complete', {
    visitor_id: visitor.visitorId,
    visit_count: visitor.visitCount,
    completion_time_seconds: totalTime,
    ...formData,
  })
}
